import type { AgentClient, AgentGrant, AgentManagementReason, AgentScope, AgentWriteScope } from './api';

/**
 * Regras puras da página "Agentes de IA" (change platform-mcp-server, D28, "Gestão no
 * backoffice"): escopos e rótulos, motivo da lista fechada, validação do detalhe e os destaques
 * de cada perfil. A API revalida tudo; aqui só se decide o que a tela mostra e o que ela deixa
 * enviar.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Os escopos em ordem de nível: `metrics` < `stores` < `billing`. */
export const AGENT_SCOPES: readonly AgentScope[] = [
  'platform.mcp.metrics.read',
  'platform.mcp.stores.read',
  'platform.mcp.billing.read',
];

/** Rótulo curto de cada escopo, para listas e seletores. */
export const SCOPE_LABELS: Record<AgentScope, string> = {
  'platform.mcp.metrics.read': 'Métricas agregadas',
  'platform.mcp.stores.read': 'Lojas',
  'platform.mcp.billing.read': 'Cobrança',
};

/** O que cada escopo libera, com o texto da tela de consentimento (`PlatformAgentScopeLabels`). */
export const SCOPE_DESCRIPTIONS: Record<AgentScope, string> = {
  'platform.mcp.metrics.read':
    'Métricas agregadas da plataforma (visão geral, origens de cadastro, série de pedidos e planos), sem loja identificada',
  'platform.mcp.stores.read':
    'Lojas por id e nome fantasia (lista, visão da loja e alertas), sem contato e sem campos de cobrança',
  'platform.mcp.billing.read': 'Campos de cobrança e comerciais por loja e o bloco agregado de cobrança',
};

/**
 * A concessão tem escopo de escrita (change platform-mcp-writes)? Qualquer `platform.mcp.*` fora dos
 * três de leitura conta: o `propose`, o `write` e o `full`. É o que liga "Suspender escritas" e o
 * rótulo "Revogar e cancelar pendentes" no cartão do perfil.
 */
export function hasWriteScopes(scopes: readonly string[]): boolean {
  return scopes.some((scope) => scope.startsWith('platform.mcp.') && !(AGENT_SCOPES as readonly string[]).includes(scope));
}

/** O texto que a revogação em massa pede digitado, exatamente. */
export const REVOKE_ALL_WRITES_CONFIRMATION = 'REVOGAR ESCRITAS';

/** Nível de um conjunto de escopos: 0 sem escopo, 1 `metrics`, 2 `stores`, 3 `billing`. */
export function scopeLevel(scopes: readonly string[]): number {
  let level = 0;
  AGENT_SCOPES.forEach((scope, index) => {
    if (scopes.includes(scope)) level = Math.max(level, index + 1);
  });
  return level;
}

/** O conjunto hierárquico de um nível: o nível 2 é `metrics` e `stores`. */
export function scopesUpToLevel(level: number): AgentScope[] {
  return AGENT_SCOPES.slice(0, Math.max(0, Math.min(level, AGENT_SCOPES.length)));
}

/** Rótulo de um conjunto hierárquico, do nível mais baixo ao mais alto. */
export function describeScopes(scopes: readonly string[]): string {
  const level = scopeLevel(scopes);
  if (level === 0) return 'nenhum';
  return scopesUpToLevel(level)
    .map((scope) => SCOPE_LABELS[scope])
    .join(' + ');
}

/** Motivos da lista fechada da gestão, na ordem do contrato. */
export const MANAGEMENT_REASONS: ReadonlyArray<{ value: AgentManagementReason; label: string }> = [
  { value: 'scheduled', label: 'Rotina agendada' },
  { value: 'suspected_leak', label: 'Suspeita de vazamento' },
  { value: 'owner_change', label: 'Troca de responsável' },
  { value: 'scope_change', label: 'Mudança de escopo' },
  { value: 'other', label: 'Outro' },
];

export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return '—';
  // Fora da lista que o Admin escolhe: o próprio titular revogou a concessão pessoal (tarefa 4.3).
  if (reason === 'subject_self_revoked') return 'Revogada pelo próprio titular';
  return MANAGEMENT_REASONS.find((r) => r.value === reason)?.label ?? reason;
}

export function isManagementReason(value: string): value is AgentManagementReason {
  return MANAGEMENT_REASONS.some((r) => r.value === value);
}

/** Limite do `detail` na API (`PlatformAgentClientValidator.ValidateDetail`). */
export const DETAIL_MAX_LENGTH = 200;

/**
 * Valida o detalhe opcional como a API: até 200 caracteres e sem `vmp` em qualquer caixa (o
 * prefixo dos tokens do agente, para ninguém colar um token na auditoria). Devolve a mensagem
 * de erro, ou `null` quando o detalhe pode ir.
 */
export function validateDetail(detail: string): string | null {
  if (detail.length > DETAIL_MAX_LENGTH) {
    return `O detalhe aceita até ${DETAIL_MAX_LENGTH} caracteres.`;
  }
  if (/vmp/i.test(detail)) {
    return 'O detalhe não pode conter "vmp". Nunca cole token do agente aqui.';
  }
  return null;
}

/** A confirmação embutida exige o nome do perfil digitado exatamente, sem aparar nem trocar caixa. */
export function confirmsProfile(typed: string, profileName: string): boolean {
  return typed.length > 0 && typed === profileName;
}

/** Concessão que vence em menos disso ganha destaque (alerta D-14 do design). */
export const EXPIRY_WARNING_DAYS = 14;
/** Refresh ocioso: 7 dias sem refresh, com o `RefreshIdleDays` padrão de 14, deixam menos de 7. */
export const REFRESH_IDLE_WARNING_DAYS = 7;
/** Recertificação: o alerta do design sai em D-30. */
export const RECERTIFICATION_WARNING_DAYS = 30;

export type AgentHighlightKind =
  | 'no_grant'
  | 'expiring'
  | 'refresh_idle'
  | 'recertification_due'
  | 'catalog_ahead';

export interface AgentHighlight {
  kind: AgentHighlightKind;
  label: string;
}

function msUntil(iso: string, now: Date): number {
  return new Date(iso).getTime() - now.getTime();
}

/**
 * Versão de catálogo mais nova que a página conhece. O `AgentClientDto` não traz a versão
 * corrente do catálogo, então ela é inferida pelo maior `catalogVersion` entre as concessões
 * listadas (ativas e histórico) e as ativas dos clientes. É um piso: uma ferramenta nova que
 * ninguém aprovou ainda não aparece aqui (resíduo registrado no tasks.md, S-2.16).
 */
export function latestKnownCatalogVersion(
  clients: readonly AgentClient[],
  grants: readonly AgentGrant[],
): number | null {
  const versions = [
    ...grants.map((g) => g.catalogVersion),
    ...clients.flatMap((c) => (c.activeGrant ? [c.activeGrant.catalogVersion] : [])),
  ];
  return versions.length === 0 ? null : Math.max(...versions);
}

/**
 * Destaques de um perfil. Só o perfil habilitado recebe "sem concessão" e o aviso de
 * recertificação: o desabilitado não conecta de qualquer jeito, e o destaque mandaria o Admin
 * ao Hermes à toa. As fronteiras são estritas: vencer em 13 dias destaca, em 14 não.
 */
export function agentHighlights(
  client: AgentClient,
  now: Date,
  latestCatalogVersion: number | null = null,
): AgentHighlight[] {
  const highlights: AgentHighlight[] = [];
  const enabled = client.status === 'Enabled';
  const grant = client.activeGrant;

  if (enabled && !grant) {
    highlights.push({ kind: 'no_grant', label: 'sem concessão' });
  }

  if (grant) {
    const untilExpiry = msUntil(grant.expiresAt, now);
    if (untilExpiry < EXPIRY_WARNING_DAYS * DAY_MS) {
      const days = Math.max(0, Math.floor(untilExpiry / DAY_MS));
      highlights.push({
        kind: 'expiring',
        label: days === 0 ? 'concessão vence hoje' : `concessão vence em ${days} ${days === 1 ? 'dia' : 'dias'}`,
      });
    }
    if (msUntil(grant.refreshIdleExpiresAt, now) < REFRESH_IDLE_WARNING_DAYS * DAY_MS) {
      highlights.push({ kind: 'refresh_idle', label: 'refresh ocioso' });
    }
    if (latestCatalogVersion !== null && grant.catalogVersion < latestCatalogVersion) {
      highlights.push({ kind: 'catalog_ahead', label: 'catálogo à frente da concessão' });
    }
  }

  if (enabled) {
    const untilDue = msUntil(client.certificationDueAt, now);
    if (untilDue < 0) {
      highlights.push({ kind: 'recertification_due', label: 'recertificação vencida' });
    } else if (untilDue < RECERTIFICATION_WARNING_DAYS * DAY_MS) {
      highlights.push({ kind: 'recertification_due', label: 'recertificação próxima' });
    }
  }

  return highlights;
}

/** Rótulo pt-BR do status do cliente. */
export function clientStatusLabel(status: string): string {
  if (status === 'Enabled') return 'Habilitado';
  if (status === 'Disabled') return 'Desabilitado';
  return status;
}

/** Rótulo pt-BR do status da concessão. */
export function grantStatusLabel(status: string): string {
  if (status === 'Active') return 'Ativa';
  if (status === 'Revoked') return 'Revogada';
  if (status === 'Expired') return 'Vencida';
  return status;
}

const REVOKE_REASON_LABELS: Record<string, string> = {
  admin: 'revogada no backoffice',
  superseded: 'substituída por concessão nova',
  refresh_reuse: 'reuso de refresh',
  code_reuse: 'reuso de code',
  client_disabled: 'perfil desabilitado',
  owner_revoked: 'conta que aprovou revogada',
  owner_demoted: 'conta que aprovou rebaixada',
  owner_reset: 'senha ou MFA de quem aprovou redefinidos',
  owner_reconciled: 'reconciliação da conta que aprovou',
  manual_sql: 'SQL de emergência',
  pepper_rotated: 'rotação do pepper',
  // Cliente pessoal do operador (change platform-mcp-operator-access, OA-D5).
  subject_revoked: 'conta titular revogada',
  subject_demoted: 'papel da conta titular sem escopo do teto',
  subject_reset: 'senha ou MFA da conta titular redefinidos',
  subject_locked: 'conta titular bloqueada',
};

export function revokeReasonLabel(reason: string | null | undefined): string {
  if (!reason) return '—';
  return REVOKE_REASON_LABELS[reason] ?? reason;
}

// =============================================================================
//  Teto com escrita (change platform-mcp-writes, tarefa 1.18, "Teto ampliado com espera")
// =============================================================================

/** Os escopos de escrita reservados, na ordem do catálogo (`PlatformMcpWriteScopes.Reserved`). */
export const WRITE_SCOPES: readonly AgentWriteScope[] = [
  'platform.mcp.trial.propose',
  'platform.mcp.trial.write',
  'platform.mcp.concessions.propose',
  'platform.mcp.concessions.write',
  'platform.mcp.plans.propose',
  'platform.mcp.notes.write',
  'platform.mcp.adjustments.propose',
  'platform.mcp.discounts.propose',
  'platform.mcp.full',
];

export function isWriteScope(scope: string): scope is AgentWriteScope {
  return (WRITE_SCOPES as readonly string[]).includes(scope);
}

/** Os escopos de escrita de um teto, na ordem do catálogo. */
export function writeScopesOf(scopes: readonly string[]): AgentWriteScope[] {
  return WRITE_SCOPES.filter((scope) => scopes.includes(scope));
}

/** Rótulo de um teto: a leitura hierárquica e, quando houver, a escrita pelos ids. */
export function describeCeiling(scopes: readonly string[]): string {
  const write = writeScopesOf(scopes);
  const read = describeScopes(scopes);
  return write.length === 0 ? read : `${read} + escrita: ${write.join(', ')}`;
}

/** O corpo `maxScopes` de um teto: a leitura do nível e a escrita marcada, na ordem do catálogo. */
export function ceilingScopes(level: number, write: readonly string[]): (AgentScope | AgentWriteScope)[] {
  return [...scopesUpToLevel(level), ...writeScopesOf(write)];
}

/**
 * Como a API classifica o pedido de teto contra o teto vigente
 * (`PlatformAgentClientCeilingWait.Classify`): `unchanged` (mesmo teto; com subida pendente, a
 * cancela), `immediate` (leitura para cima ou para baixo, ou escrita para baixo), `write_raise`
 * (acrescenta escrita e nada mais: fica pendente por 1 h) e `mixed` (acrescenta escrita e mexe em
 * outra coisa: a API recusa com `ceiling_write_raise_not_alone`).
 */
export type CeilingChangeKind = 'unchanged' | 'immediate' | 'write_raise' | 'mixed';

export function classifyCeilingChange(current: readonly string[], requested: readonly string[]): CeilingChangeKind {
  const known: readonly string[] = [...AGENT_SCOPES, ...WRITE_SCOPES];
  const have = current.filter((scope) => known.includes(scope));
  const added = requested.filter((scope) => !have.includes(scope));
  const removed = have.filter((scope) => !requested.includes(scope));
  if (added.length === 0 && removed.length === 0) return 'unchanged';
  if (!added.some(isWriteScope)) return 'immediate';
  return added.every(isWriteScope) && removed.length === 0 ? 'write_raise' : 'mixed';
}
